import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, Linkedin, Shield } from "lucide-react";
import { BRAND, NAV_LINKS } from "../lib/constants";

export default function Footer() {
  return (
    <footer
      className="bg-navy text-ivory relative overflow-hidden"
      data-testid="site-footer"
    >
      {/* Subtle gold rule */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold to-transparent" />

      <div className="container-luxe py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* Brand */}
          <div className="md:col-span-5">
            <Link to="/" className="flex items-center gap-3 mb-6" data-testid="footer-logo">
              <img
                src={BRAND.logoUrl}
                alt="AthenaCrest Defense"
                loading="lazy"
                decoding="async"
                className="h-14 w-auto"
              />
              <div>
                <div className="font-serif text-2xl">AthenaCrest</div>
                <div className="font-sans text-xs tracking-overline uppercase text-gold">
                  Defense
                </div>
              </div>
            </Link>
            <p className="font-serif italic text-xl text-ivory/85 leading-relaxed max-w-md mb-8">
              "We help facilities solve expensive operational problems before
              they become lawsuits, survey tags, revenue loss, or reputational
              damage."
            </p>
            <a
              href={BRAND.calendlyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold"
              data-testid="footer-cta-book"
            >
              Book a Confidential Consultation
            </a>
          </div>

          {/* Navigation */}
          <div className="md:col-span-3">
            <div className="overline mb-5">Explore</div>
            <ul className="space-y-3">
              {NAV_LINKS.slice(0, 6).map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="font-sans text-sm text-ivory/75 hover:text-gold transition-colors"
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="overline mb-5">Practice</div>
            <ul className="space-y-3">
              {NAV_LINKS.slice(6).map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="font-sans text-sm text-ivory/75 hover:text-gold transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-2">
            <div className="overline mb-5">Contact</div>
            <ul className="space-y-3 text-sm font-sans text-ivory/75">
              <li className="flex items-start gap-2">
                <Mail className="h-4 w-4 text-gold mt-0.5 flex-shrink-0" />
                <a href={`mailto:${BRAND.email}`} className="hover:text-gold">
                  {BRAND.email}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <Phone className="h-4 w-4 text-gold mt-0.5 flex-shrink-0" />
                <a href={`tel:${BRAND.phone}`} className="hover:text-gold">
                  {BRAND.phone}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <Linkedin className="h-4 w-4 text-gold mt-0.5 flex-shrink-0" />
                <a
                  href={BRAND.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-gold"
                >
                  Sierra Thomas
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-ivory/15">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
            <p className="text-xs font-sans text-ivory/55 max-w-3xl leading-relaxed">
              <Shield className="inline h-3.5 w-3.5 text-gold mr-2 -mt-0.5" />
              AthenaCrest Defense provides healthcare operations and compliance
              consulting services. Services do not constitute legal advice or
              establish an attorney-client relationship. Based in St. Louis,
              Missouri. Serving facilities across Missouri.
            </p>
            <p className="text-xs font-sans text-ivory/55 whitespace-nowrap">
              © {new Date().getFullYear()} AthenaCrest Defense. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
